import React from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import moment from 'moment';
import { styled } from '@mui/material';

import { NoFiles } from 'components/organisms/Files';
import { useAuth } from 'contexts/auth';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Container,
    Icon,
    IconButton,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextInput,
    Link,
} from 'shell/components';
import {
    downloadFileFromURL,
    getAbsoluteURL,
    getFileNameExt,
    getPrettyFileSize,
    getPrettyMIMEType,
    getResourceName,
    getS3FileURL,
    getTooltipComponent,
    getUserPreferredFullName,
} from 'utils/helper';

const renderResourceType = (resource) => {
    return resource.url ? 'Website Link' : getPrettyMIMEType(resource.data.type) || getFileNameExt(resource.data.name);
};

const renderUploader = (uploader) => {
    return uploader ? (
        <Stack alignItems={'center'} direction={'row'} spacing={2}>
            <Avatar size={'small'} user={uploader} />
            {getUserPreferredFullName(uploader)}
        </Stack>
    ) : (
        ''
    );
};

const renderResourceSize = (resource) => {
    if (resource.url) return '-';
    return resource.data.size ? getPrettyFileSize(resource.data.size, true) : 'Unknown';
};

const Desktop = ({
    isLoading,
    resources,
    accountUsers,
    checked,
    setChecked,
    setUpload,
    filters,
    setFilters,
    numFilters,
    onDelete,
    onDeleteMany,
    getResourceURL,
    renderResourceActions,
}) => {
    const { auth } = useAuth();

    const handleClickDownload = (resource) => {
        if (resource.url) {
            downloadFileFromURL(getAbsoluteURL(resource.url), resource.name);
        } else {
            downloadFileFromURL(getS3FileURL(resource.data), resource.data.name);
        }
    };

    const handleClickDelete = (resource) => {
        onDelete(resource);
    };

    const renderResource = (resource) => {
        const uploader = accountUsers.find(({ owner_account_id }) => owner_account_id === resource.owner_account_id);
        return (
            <>
                <TableCell sx={{ paddingRight: 0 }}>
                    <Checkbox
                        aria-label={`select ${resource.name || resource.data.name}`}
                        checked={checked.includes(resource.id)}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setChecked((current) => [...current, resource.id]);
                            } else {
                                setChecked((current) => current.filter((item) => item !== resource.id));
                            }
                        }}
                    />
                </TableCell>
                <TableCell noWrap>
                    <CustomLink external href={getResourceURL(resource)} rel={'noopener noreferrer'} target={'_blank'}>
                        {getResourceName(resource)}
                    </CustomLink>
                </TableCell>
                <TableCell>{renderResourceType(resource)}</TableCell>
                <TableCell>{renderUploader(uploader)}</TableCell>
                <TableCell>{moment(resource.created_on).format('MMM D [at] hh:mma')}</TableCell>
                <TableCell>{renderResourceSize(resource)}</TableCell>
                <TableCell>
                    {renderResourceActions(
                        resource,
                        () => handleClickDownload(resource),
                        () => handleClickDelete(resource)
                    )}
                </TableCell>
            </>
        );
    };

    const PageInner = (
        <CustomBox role={auth.role}>
            <Stack direction={'row'} justifyContent={'space-between'}>
                <Stack direction={'row'} spacing={4}>
                    <Button
                        onClick={() => setUpload({ open: true })}
                        tooltip={{
                            component: getTooltipComponent(
                                'Public to Your Team',
                                'Upload files and links for your team to view.'
                            ),
                        }}
                    >
                        Upload
                    </Button>
                    {checked.length ? (
                        <Button color={'destructive'} variant={'secondary'} onClick={onDeleteMany}>
                            Delete ({checked.length})
                        </Button>
                    ) : null}
                </Stack>
                <Stack direction={'row'} spacing={4}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={() => setFilters((current) => ({ ...current, open: true }))}
                    >
                        Filters {numFilters ? `(${numFilters})` : ''}
                    </Button>
                    <CustomTextInput
                        disablePointerEventsOnIcon={!Boolean(filters.searchInput)}
                        endIcon={
                            filters.searchInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => setFilters((current) => ({ ...current, searchInput: '' }))}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={filters.searchInput}
                        onChange={(e) => setFilters((current) => ({ ...current, searchInput: e.target.value }))}
                    />
                </Stack>
            </Stack>
            {isLoading ? (
                <CustomLogoLoading role={auth.role} />
            ) : (
                <>
                    <CustomTable size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ paddingRight: 0 }}>
                                    <Checkbox
                                        aria-label={'select all resources'}
                                        checked={
                                            Boolean(resources.length) &&
                                            resources.every(({ id }) => checked.includes(id))
                                        }
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setChecked(resources.map(({ id }) => id));
                                            } else {
                                                setChecked([]);
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Uploaded By</TableCell>
                                <TableCell>Uploaded On</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {resources.map((resource) => (
                                <TableRow key={resource.id}>{renderResource(resource)}</TableRow>
                            ))}
                        </TableBody>
                    </CustomTable>
                    {!resources.length ? <NoFiles /> : null}
                </>
            )}
        </CustomBox>
    );

    return auth.role === 'instructor' ? PageInner : <Container device={'desktop'}>{PageInner}</Container>;
};

const CustomBox = styled(Box)(({ role, theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        width: '100%',
        minHeight: 'calc(100vh - 64px - 20px)',
        padding: theme.spacing(5, 5, 8),
        background: theme.palette.ensightful.shades[0],
        ...(role === 'instructor' && {
            minHeight: 'calc(100vh - 64px - 20px - 32px - 16px - 34px - 8px)',
        }),
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '13.5rem',
    };
});

const CustomLogoLoading = styled(LogoLoading)(({ role }) => {
    return {
        ...(role === 'instructor' && {
            height: '50vh',
        }),
    };
});

const CustomTable = styled(Table)(({ theme }) => {
    return {
        tableLayout: 'fixed',
        marginTop: theme.spacing(2),
        '& > thead > tr > th:nth-of-type(1)': {
            width: '40px',
        },
        '& > thead > tr > th:nth-of-type(2)': {},
        '& > thead > tr > th:nth-of-type(3)': {
            width: '15%',
        },
        '& > thead > tr > th:nth-of-type(4)': {
            width: '15%',
        },
        '& > thead > tr > th:nth-of-type(5)': {
            width: '15%',
        },
        '& > thead > tr > th:nth-of-type(6)': {
            width: '10%',
        },
        '& > thead > tr > th:nth-of-type(7)': {
            width: '88px',
        },
        '& > tbody > tr > td:nth-of-type(1)': {
            width: '40px',
        },
        '& > tbody > tr > td:nth-of-type(2)': {},
        '& > tbody > tr > td:nth-of-type(3)': {
            width: '15%',
        },
        '& > tbody > tr > td:nth-of-type(4)': {
            width: '15%',
        },
        '& > tbody > tr > td:nth-of-type(5)': {
            width: '15%',
        },
        '& > tbody > tr > td:nth-of-type(6)': {
            width: '10%',
        },
        '& > tbody > tr > td:nth-of-type(7)': {
            width: '88px',
        },
    };
});

const CustomLink = styled(Link)(({ theme }) => {
    return {
        color: theme.palette.ensightful.neutral[900],
        textDecoration: 'none',

        '&:hover': {
            textDecoration: 'underline',
        },
    };
});

export default Desktop;
